/**
 * Dates, in one place. Gregorian with Arabic month names and Arabic-Indic
 * digits on the human surface; ISO-8601 ASCII in the `<time datetime>`
 * attribute on the **same element**. Time zone `Asia/Aden` on human
 * surfaces, UTC on the wire.
 *
 * **There is no second calendar in this product — do not add a converter.**
 *
 * Every sort over a date reads the machine value, never the rendered
 * «قبل يومين».
 */

const HUMAN_LOCALE = 'ar-YE';
const DISPLAY_TIME_ZONE = 'Asia/Aden';

const absoluteDate = new Intl.DateTimeFormat(HUMAN_LOCALE, {
  calendar: 'gregory',
  timeZone: DISPLAY_TIME_ZONE,
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const absoluteDateTime = new Intl.DateTimeFormat(HUMAN_LOCALE, {
  calendar: 'gregory',
  timeZone: DISPLAY_TIME_ZONE,
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const relative = new Intl.RelativeTimeFormat(HUMAN_LOCALE, { numeric: 'auto' });

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

export function parseDate(iso: string): Date {
  return new Date(iso);
}

/** «٣١ يوليو ٢٠٢٦» */
export function formatDate(iso: string): string {
  return absoluteDate.format(parseDate(iso));
}

/** «٣١ يوليو ٢٠٢٦، ١٩:٣٠» */
export function formatDateTime(iso: string): string {
  return absoluteDateTime.format(parseDate(iso));
}

/**
 * The machine half. Always ASCII, always ISO-8601, always the value that goes
 * into `<time datetime>` beside whichever human string is rendered.
 */
export function toMachineDate(iso: string): string {
  return parseDate(iso).toISOString();
}

/**
 * Relative inside a week, absolute after it. `now` is injected so the
 * behaviour is testable without freezing the clock.
 */
export function formatRelativeOrAbsolute(iso: string, now: Date = new Date()): string {
  const value = parseDate(iso);
  const delta = value.getTime() - now.getTime();
  const magnitude = Math.abs(delta);

  if (magnitude >= WEEK) return formatDate(iso);

  if (magnitude >= DAY) return relative.format(Math.round(delta / DAY), 'day');
  if (magnitude >= HOUR) return relative.format(Math.round(delta / HOUR), 'hour');
  if (magnitude >= MINUTE) return relative.format(Math.round(delta / MINUTE), 'minute');

  return relative.format(Math.round(delta / 1000), 'second');
}
